import type { CSSProperties } from 'react';

export interface AuditEntry {
  at: string;
  actor: string;
  actor_role: string;
  event_type: string;
  payload: Record<string, unknown>;
}

export function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 0) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function formatAuditEvent(entry: AuditEntry): string {
  const actor = entry.actor;
  const p = entry.payload as Record<string, unknown>;
  switch (entry.event_type) {
    case 'user_login':
      return `${actor} signed in`;
    case 'dv_config_change': {
      const scope = p['scope'] === 'repo_override' ? `${p['org']}/${p['repo']}` : `${p['org']} (default)`;
      return `${actor} changed DV config for ${scope}`;
    }
    case 'org_opted_in':
      return `${actor} opted ${p['org']} into Diff Vader`;
    case 'org_opted_out':
      return `${actor} opted ${p['org']} out of Diff Vader`;
    case 'redundant_overrides_cleaned':
      return `${actor} cleaned ${p['removed_count']} redundant override(s) in ${p['org']}`;
    case 'rbac_grant':
      return `${actor} set ${p['target_email']} to ${p['new_role']}`;
    case 'rbac_revoke':
      return `${actor} revoked ${p['target_email']}'s ${p['old_role']} role`;
    case 'quarterly_signoff':
      return `${actor} signed off the ${p['quarter']} review for ${p['org']} (revision ${p['revision']})`;
    case 'quarterly_note':
      return `${actor} added a review note to ${p['quarter']} for ${p['org']}`;
    case 'annotation_created':
      return `${actor} annotated ${p['effective_date']} (${p['reviewer'] === '*' ? 'all reviewers' : p['reviewer']})`;
    default:
      return `${actor} performed ${entry.event_type}`;
  }
}

interface Props {
  events: AuditEntry[];
  isLoading?: boolean;
  error?: unknown;
  title?: string;
}

export function AuditLogPanel({ events, isLoading, error, title = 'Recent activity' }: Props) {
  const noun = title.toLowerCase();
  return (
    <div style={{ marginTop: 'var(--loop-space-300)', borderTop: '1px solid var(--loop-neutral-300)', paddingTop: 'var(--loop-space-200)' }}>
      <div style={headerStyle}>{title}</div>
      {isLoading ? (
        <div style={muted}>Loading {noun}…</div>
      ) : error ? (
        <div style={muted}>Couldn&apos;t load {noun} — {(error as Error).message}</div>
      ) : events.length === 0 ? (
        <div style={muted}>No activity yet — events appear when configuration changes are made or users sign in.</div>
      ) : (
        events.map((e) => (
          <div key={`${e.at}-${e.actor}-${e.event_type}`} style={row}>
            <span style={tsCol}>{relativeTime(e.at)}</span>
            <span style={textCol}>{formatAuditEvent(e)}</span>
          </div>
        ))
      )}
    </div>
  );
}

const headerStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 528,
  color: 'var(--loop-neutral-700)',
  marginBottom: 'var(--loop-space-100)',
  paddingBottom: 'var(--loop-space-075)',
  borderBottom: '1px solid var(--loop-neutral-300)',
};
const muted: CSSProperties = { fontSize: '0.875rem', color: 'var(--loop-neutral-600)', padding: 'var(--loop-space-100) 0' };
const row: CSSProperties = { display: 'flex', gap: 'var(--loop-space-150)', padding: 'var(--loop-space-075) 0', borderBottom: '1px solid var(--loop-neutral-100)', fontSize: '0.8rem' };
const tsCol: CSSProperties = { color: 'var(--loop-neutral-600)', whiteSpace: 'nowrap', minWidth: 64 };
const textCol: CSSProperties = { color: 'var(--loop-neutral-900)' };
